import { EVALUATION_TEMPLATES } from '../../constants/alkymation';

/**
 * Template-specific mapping configurations
 * Note: Field keys should match the parameter IDs from the template definition
 */
const TEMPLATE_MAPPERS = {
  [EVALUATION_TEMPLATES.ASSESSMENT_INVITATION]: {
    fields: {
      screeningId: (screening) => screening?.screeningId || screening?.id,
      limitDate: (screening) => {
        // Always default to 7 days from today
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 7);
        defaultDate.setHours(23, 59, 0, 0);
        return defaultDate.toISOString();
      },
      levelId: (screening) => String(screening?.levelId || ''),
      screeningName: (screening) => screening?.recruitSessionStageName || screening?.name || '',
      recruitSessionStageId: (screening) => String(screening?.recruitSessionStageId || screening?.id || ''),
      workspaceId: (screening, context) => String(context?.workspace || screening?.workspaceId || ''),
    },
    requiresContext: ['workspace'],
  },
  // Add more template mappings here as needed
};

/**
 * Maps screening data to node parameters based on template type
 * @param {string} templateId - The template identifier
 * @param {Object} screening - The screening object containing source data
 * @param {Object} context - Additional context data (workspace, user, etc.)
 * @returns {Object} Mapped parameters for the node form
 */
export const mapScreeningToNodeParams = (templateId, screening, context = {}) => {
  const mapper = TEMPLATE_MAPPERS[templateId];

  if (!mapper) {
    // Default mapping for templates without specific mapper
    return {
      screeningId: screening?.screeningId || screening?.id,
    };
  }

  const mappedParams = {};

  // Apply field mappings
  Object.entries(mapper.fields).forEach(([key, mappingFn]) => {
    if (typeof mappingFn === 'function') {
      mappedParams[key] = mappingFn(screening, context);
    }
  });

  return mappedParams;
};

/**
 * Checks if a template requires additional context for mapping
 * @param {string} templateId - The template identifier
 * @returns {Array} List of required context fields
 */
export const getRequiredContext = (templateId) => {
  const mapper = TEMPLATE_MAPPERS[templateId];
  return mapper?.requiresContext || [];
};

/**
 * Validates if all required context is present
 * @param {string} templateId - The template identifier
 * @param {Object} context - The context object to validate
 * @returns {boolean} True if all required context is present
 */
export const hasRequiredContext = (templateId, context) => {
  const required = getRequiredContext(templateId);
  return required.every((key) => context && context[key] !== undefined);
};

/**
 * Adds a new template mapper configuration
 * @param {string} templateId - The template identifier
 * @param {Object} mapperConfig - The mapping configuration
 */
export const registerTemplateMapper = (templateId, mapperConfig) => {
  if (!templateId || !mapperConfig) {
    throw new Error('Template ID and mapper configuration are required');
  }

  TEMPLATE_MAPPERS[templateId] = mapperConfig;
};

/**
 * Gets all available template mappers
 * @returns {Object} All template mappers
 */
export const getTemplateMappers = () => ({ ...TEMPLATE_MAPPERS });

export default {
  mapScreeningToNodeParams,
  getRequiredContext,
  hasRequiredContext,
  registerTemplateMapper,
  getTemplateMappers,
};